// Kiểu dữ liệu + tiện ích cho màn Hoa hồng CTV (GĐ8, §2.5) — Đại lý (bảng cơ chế, CTV của tôi)
// và CTV (tiến độ tháng). API: apps/api/src/routes/commissions.ts.

import type { CommissionMode, CommissionSchemeDef, CommissionTier, TierProgress } from '@shoptrack/common/commission-scheme'

export type { CommissionMode, CommissionSchemeDef, CommissionTier, TierProgress }

export const MODE_LABEL: Record<CommissionMode, string> = {
  progressive: 'Lũy tiến theo đơn hàng',
}

export const MODE_SHORT: Record<CommissionMode, string> = {
  progressive: 'Lũy tiến theo đơn hàng',
}

export interface SchemeVersion {
  version: number
  mode: CommissionMode
  tiers: CommissionTier[]
  effectiveFromMonth: string
  createdAt: string
}

export interface SchemeAssignment {
  ctvShopId: string
  effectiveFromMonth: string
  payerKey: string
}

export interface Scheme {
  id: string
  code: string
  name: string
  /** Bản mới nhất (có thể là bản hẹn từ tháng sau). */
  mode: CommissionMode
  tiers: CommissionTier[]
  version: number
  archivedAt: string | null
  createdAt?: string
  versions: SchemeVersion[]
  assignments: SchemeAssignment[]
  /** Số CTV đang dùng bảng tháng này / sẽ dùng từ tháng sau (máy chủ tính). */
  inUse?: number
  inUseNextMonth?: number
}

export interface CapCheck {
  ok: boolean
  maxRate: number
  minMargin: number
  /** Ô bảng giá quyết định chênh lệch nhỏ nhất, vd "Nội miền · 3kg". */
  at: string
}

export interface CtvRow {
  shopId: string
  name: string
  status: string
  scheme: { id: string; code: string; name: string; payerKey: string; effectiveFromMonth: string } | null
  nextScheme: { id: string; code: string; name: string; effectiveFromMonth: string } | null
  totalOrders: number
  accrued: number
  monthStatus: 'provisional' | 'closed' | null
  /** Số đơn hôm nay (bậc đếm theo ngày); null khi xem tháng cũ. */
  todayOrders?: number | null
  progress: TierProgress | null
  /** Đã rời (nâng lên Đại lý / chuyển nhánh) — còn hiện vì có hoa hồng tháng đang xem */
  left?: boolean
  /** Trần hoa hồng (D25): chênh lệch nhỏ nhất của Đại lý trên CTV này, theo giá hiện hành. */
  cap?: { minMargin: number; at: string } | null
}

export interface CommissionLine {
  id: string
  waybillCode: string | null
  shopId: string | null
  /** Tên shop của đơn (máy chủ trả kèm). */
  shopName?: string | null
  deliveredAt: string | null
  /** BEST lấy hàng — mốc có hoa hồng. */
  pickedUpAt?: string | null
  /** 'base' = ghi theo đơn · 'topup' = bù cuối tháng (chế độ cũ, đã bỏ) */
  sourceRef: string
  amount: number
  status: string
  holdReason: string | null
  note: string | null
  createdAt: string
}

/** Một ngày (giờ VN, theo ngày TẠO đơn) — bậc đếm lại từ đầu mỗi ngày. */
export interface CommissionDay {
  day: string
  /** Số đơn được xếp bậc (không gồm đơn hủy trước khi lấy hàng). */
  orders: number
  /** BEST đã lấy hàng → đã có hoa hồng (giao, hoàn hay thất lạc đều giữ). */
  pickedUp: number
  /** Ước tính nếu mọi đơn đều được lấy hàng, theo mức bậc của thứ tự trong ngày. */
  estimated: number
  /** Đã tính (đơn BEST đã lấy hàng). */
  credited: number
}

export interface MyCommission {
  month: string
  scheme: { id: string; code: string; name: string; payerKey: string } | null
  def: CommissionSchemeDef | null
  /** Số đơn đã có hoa hồng trong tháng (BEST đã lấy hàng). */
  totalOrders: number
  /** Hoa hồng đã tính (cộng dồn, chốt theo tháng). */
  accrued: number
  /** Ước tính cả tháng nếu mọi đơn đã tạo đều được lấy hàng. */
  estimated: number
  /** 'empty' = tháng đã qua không có đơn tính hoa hồng. */
  status: 'provisional' | 'closed' | 'empty'
  /** Tiến độ bậc HÔM NAY; null khi xem tháng cũ. */
  progress: TierProgress | null
  today: CommissionDay | null
  /** Các ngày trong tháng có đơn, mới nhất trước. */
  days: CommissionDay[]
}

export interface MyCommissionMonth {
  code: string
  month: string
  totalOrders: number
  amount: number
  status: string
  mode: CommissionMode
  /** Bên trả: tên NPP của đơn vị (Sunrise: "ShopTrack") hoặc "Đại lý". */
  payer: string
}

export const LINE_STATUS: Record<string, { text: string; cls: string }> = {
  estimated: { text: 'Tạm tính', cls: 'bg-info/12 text-info' },
  held: { text: 'Tạm giữ', cls: 'bg-warn/12 text-warn' },
  payable: { text: 'Sẵn sàng chi', cls: 'bg-ok/12 text-ok' },
  in_voucher: { text: 'Đã lên phiếu', cls: 'bg-accent/12 text-accent' },
  paid: { text: 'Đã chi', cls: 'bg-ok/12 text-ok' },
  booked: { text: 'Đã ghi', cls: 'bg-paperSoft text-inkSoft' },
  void: { text: 'Đã hủy', cls: 'bg-paperSoft text-inkSoft' },
}

export const MONTH_STATUS_LABEL: Record<string, string> = {
  provisional: 'Tạm tính',
  open: 'Tạm tính',
  closed: 'Đã chốt',
  empty: 'Không phát sinh',
}

// ─── Tháng (YYYY-MM, theo giờ Việt Nam như máy chủ) ──────────────────

const VN_OFFSET_MS = 7 * 60 * 60 * 1000

export function currentMonthVN(now = Date.now()): string {
  const d = new Date(now + VN_OFFSET_MS)
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
}

export function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number) as [number, number]
  const idx = y * 12 + (m - 1) + delta
  return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, '0')}`
}

/** "2026-10" → "10/2026" */
export function monthLabel(month: string | null | undefined): string {
  if (!month) return '—'
  const [y, m] = month.split('-')
  return y && m ? `${m}/${y}` : month
}

// ─── Bậc ─────────────────────────────────────────────────────────────

export const vnd = (n: number) => `${n.toLocaleString('vi-VN')}đ`

export function tierRange(t: CommissionTier): string {
  if (t.toOrders === null) return `Từ ${t.fromOrders.toLocaleString('vi-VN')}`
  return `${t.fromOrders.toLocaleString('vi-VN')} – ${t.toOrders.toLocaleString('vi-VN')}`
}

export function tiersSummary(tiers: CommissionTier[]): string {
  return tiers.map((t) => `${tierRange(t)}: ${vnd(t.amountPerOrder)}`).join(' · ')
}

/** Bậc chứa đơn thứ max(count, 1). */
export function currentTierIndex(tiers: CommissionTier[], count: number): number {
  const k = Math.max(count, 1)
  const i = tiers.findIndex((t) => k >= t.fromOrders && (t.toOrders === null || k <= t.toOrders))
  return i === -1 ? tiers.length - 1 : i
}

/** Tiến độ trong bậc hiện tại tới bậc kế (0–1); đã ở bậc cuối = 1. */
export function tierFraction(tiers: CommissionTier[], count: number): number {
  if (!tiers.length) return 0
  const i = currentTierIndex(tiers, count)
  const cur = tiers[i]!
  const next = tiers[i + 1]
  if (!next) return 1
  const span = next.fromOrders - cur.fromOrders
  return span > 0 ? Math.min(1, Math.max(0, (count - cur.fromOrders + 1) / span)) : 0
}

/** Phiên bản đang áp ở `month` (bản mới nhất có hiệu lực ≤ month). */
export function versionAt(s: Pick<Scheme, 'versions'>, month: string): SchemeVersion | null {
  let best: SchemeVersion | null = null
  for (const v of s.versions) if (v.effectiveFromMonth <= month && (!best || v.version > best.version)) best = v
  return best
}

/**
 * Phiên bản đã hẹn cho tháng sau (nếu có) = bản sẽ áp tháng sau theo đúng quy tắc ghi hoa hồng,
 * khi nó chưa áp tháng này. Bản hẹn đã bị bản sửa sau thay (vd. giảm mức áp ngay) không hiện.
 */
export function scheduledVersion(s: Pick<Scheme, 'versions'>, month: string): SchemeVersion | null {
  const next = versionAt(s, shiftMonth(month, 1))
  return next && next.effectiveFromMonth > month ? next : null
}

/** Bản số lớn hơn, hiệu lực không muộn hơn `v` → `v` không bao giờ được áp (bị thay). */
export function supersededBy(s: Pick<Scheme, 'versions'>, v: SchemeVersion): SchemeVersion | null {
  let by: SchemeVersion | null = null
  for (const w of s.versions) if (w.version > v.version && w.effectiveFromMonth <= v.effectiveFromMonth && (!by || w.version > by.version)) by = w
  return by
}

/**
 * Ghi chú máy của dòng hoa hồng → lời thường. "k:7;rate:300" (lifecycle.ts) = đơn thứ 7 trong
 * ngày tạo đơn, mức 300đ/đơn; "topup:2026-09" = khoản bù khi chốt tháng của chế độ cũ (đã bỏ). Ghi chú máy khác không hiện.
 */
export function commissionNoteLabel(note: string | null | undefined): string | null {
  if (!note) return null
  const k = /^k:(\d+);rate:(\d+)$/.exec(note)
  if (k) return `Đơn thứ ${Number(k[1]).toLocaleString('vi-VN')} trong ngày · ${vnd(Number(k[2]))}`
  const t = /^topup:(\d{4}-\d{2})$/.exec(note)
  if (t) return `Bù khi chốt tháng ${monthLabel(t[1])}`
  return /^[a-z_]+:/i.test(note) ? null : note
}

/**
 * CTV đang dùng từng bảng ở `month` — gộp lịch sử gán của mọi bảng (một CTV có thể đã chuyển
 * bảng), lấy lần gán mới nhất có hiệu lực ≤ month.
 */
export function ctvsBySchemeAt(schemes: Scheme[], month: string): Map<string, string[]> {
  const latest = new Map<string, { schemeId: string; from: string }>()
  for (const s of schemes) {
    for (const a of s.assignments) {
      if (a.effectiveFromMonth > month) continue
      const cur = latest.get(a.ctvShopId)
      if (!cur || a.effectiveFromMonth > cur.from) latest.set(a.ctvShopId, { schemeId: s.id, from: a.effectiveFromMonth })
    }
  }
  const out = new Map<string, string[]>()
  for (const [ctv, { schemeId }] of latest) out.set(schemeId, [...(out.get(schemeId) ?? []), ctv])
  return out
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' })
}
